// DataPilot client. Talks to the reporting-api /datapilot/ask endpoint, with a
// self-contained TypeScript "demo brain" fallback so the assistant works even
// with no backend (VITE_DEMO) or when the API/LLM is unreachable.

import api, { DEMO_MODE } from './client'
import { KPI_GLOSSARY, type KPIEntry } from '../data/kpi-glossary'

export interface DPSeriesPoint {
  date: string
  value: number
}

export interface DPSnapshot {
  value?: number
  previous?: number
  change_pct?: number
  unit?: string
  currency?: string
  series?: DPSeriesPoint[]
  extra?: Record<string, number>
}

export interface DPNamedSnapshot {
  metric_id?: string
  metric_name?: string
  snapshot: DPSnapshot
}

export interface DPRequest {
  metric_id?: string
  metric_name?: string
  question?: string
  kind?: 'metric' | 'summary'
  period?: string
  locale?: string
  snapshot?: DPSnapshot
  metrics?: DPNamedSnapshot[]
}

export type DPSeverity = 'info' | 'warning' | 'critical'

export interface DPAnomaly {
  metric?: string
  severity: DPSeverity
  message: string
}

export interface DPResponse {
  answer: string
  insights?: string[]
  anomalies?: DPAnomaly[]
  suggested_questions?: string[]
  source: 'demo' | 'live'
}

// askDataPilot is the single entry point used by the UI.
export async function askDataPilot(req: DPRequest): Promise<DPResponse> {
  if (DEMO_MODE) return demoBrain(req)
  try {
    const res = await api.post('/datapilot/ask', req)
    // The Go API wraps payloads in { data: ... }.
    return (res.data?.data ?? res.data) as DPResponse
  } catch {
    return demoBrain(req)
  }
}

// ---- TypeScript demo brain (mirror of internal/datapilot/demo.go) ----------

function glossaryFor(id?: string, name?: string): KPIEntry | undefined {
  if (id) {
    const byId = KPI_GLOSSARY.find((k) => k.id === id)
    if (byId) return byId
  }
  if (name) {
    const lower = name.toLowerCase()
    return KPI_GLOSSARY.find((k) => k.name.toLowerCase() === lower)
  }
  return undefined
}

// Direction polarity by metric key fragment. true = higher is better.
const HIGHER_BETTER: Record<string, boolean> = {
  ggr: true, ngr: true, net_cash: true, deposits: true, ftd: true,
  active: true, conversion: true, retention: true, arpu: true, ltv: true,
  hold: true, margin: true, cross_sell: true,
  withdrawal: false, churn: false, bonus_cost: false, cac: false, bonus_ggr: false,
}

function polarity(metric?: string): boolean | undefined {
  if (!metric) return undefined
  for (const [frag, val] of Object.entries(HIGHER_BETTER)) {
    if (metric.includes(frag)) return val
  }
  return undefined
}

function changePct(s?: DPSnapshot): number | undefined {
  if (!s) return undefined
  if (typeof s.change_pct === 'number') return s.change_pct
  if (typeof s.value === 'number' && typeof s.previous === 'number' && s.previous !== 0) {
    return ((s.value - s.previous) / Math.abs(s.previous)) * 100
  }
  return undefined
}

function humanNumber(v: number): string {
  const abs = Math.abs(v)
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${(v / 1_000).toFixed(1)}k`
  return v.toFixed(0)
}

function formatValue(v: number, s: DPSnapshot): string {
  if (s.unit === '%') return `${v.toFixed(1)}%`
  if (s.currency) return `${s.currency} ${humanNumber(v)}`
  if (s.unit) return `${humanNumber(v)} ${s.unit}`
  return humanNumber(v)
}

// ---- localization (mirror of internal/datapilot/i18n.go) -------------------

type Loc = 'en' | 'it' | 'es' | 'pt'

function normLoc(l?: string): Loc {
  const base = (l || 'en').slice(0, 2).toLowerCase()
  return base === 'it' || base === 'es' || base === 'pt' ? (base as Loc) : 'en'
}

// fmt replaces {token} placeholders.
function fmt(tpl: string, vars: Record<string, string | number>): string {
  return tpl.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? ''))
}

const DP_PHRASES: Record<Loc, Record<string, string>> = {
  en: {
    thisMetric: 'this metric',
    genericDef: '{name} is a key metric tracked on this dashboard.',
    trend: 'It is {dir} {pct}% over {period}.',
    atValue: ' Now at {value}.',
    favourable: ' That is a favourable move.',
    unfavourable: ' That move works against you and is worth a closer look.',
    valueNow: 'It currently reads {value}.',
    up: 'up', down: 'down', flat: 'broadly flat', thePeriod: 'the period',
    volatile: '{name} has been volatile across the recent window — judge the trend over the full period, not a single day.',
    stable: '{name} has been fairly stable day to day.',
    reQuestion: 'On your question "{q}": here is what the current data shows.',
    anomSwing: '{name} moved {pct}% against the desirable direction — investigate the driver.',
    anomHold: 'Hold % is {value}%, below the ~30% guideline: payouts are high relative to deposits.',
    anomBonus: 'Bonus/GGR ratio is {value}%, above the ~30% efficiency threshold — promotions may be over-spending.',
    anomNetCash: 'Net Cash is negative this period — more money left than came in.',
    sqWhy: 'Why did {name} change?',
    sqDriver: 'What is driving {name} right now?',
    sqBenchmark: 'How does this compare to a healthy benchmark?',
    summaryIntro: 'Here is the picture for {period}.',
    summaryHeadline: 'The biggest mover is {name}, {dir} {pct}%.',
    moverLine: '{name} {dir} {pct}%.',
    sqWhyMoved: 'Why did the top metric move?',
    sqTopBrand: 'Which brand contributed most?',
    sqRisk: 'Any risks I should watch this period?',
    attrUpLead: 'The increase was driven mainly by these players:',
    attrDownLead: 'The decline traces mainly to these players:',
    attrConcentration: 'Together these {n} players account for ~{pct}% of the move.',
    attrBrandLed: '{brand} contributed the largest share of the change.',
    sqTopPlayers: 'Show the top contributing players',
  },
  it: {
    thisMetric: 'questa metrica',
    genericDef: '{name} è una metrica chiave monitorata in questa dashboard.',
    trend: 'È {dir} del {pct}% su {period}.',
    atValue: ' Ora a {value}.',
    favourable: ' È un movimento favorevole.',
    unfavourable: ' È un movimento sfavorevole e merita un approfondimento.',
    valueNow: 'Attualmente vale {value}.',
    up: 'in aumento', down: 'in calo', flat: 'sostanzialmente stabile', thePeriod: 'il periodo',
    volatile: '{name} è stata volatile nell’ultimo periodo — valuta il trend sull’intero periodo, non sul singolo giorno.',
    stable: '{name} è rimasta abbastanza stabile giorno per giorno.',
    reQuestion: 'Sulla tua domanda "{q}": ecco cosa mostrano i dati attuali.',
    anomSwing: '{name} si è mosso del {pct}% nella direzione sfavorevole — indaga la causa.',
    anomHold: 'L’Hold % è {value}%, sotto la soglia indicativa del ~30%: i pagamenti sono alti rispetto ai depositi.',
    anomBonus: 'Il rapporto Bonus/GGR è {value}%, sopra la soglia di efficienza del ~30% — le promozioni potrebbero costare troppo.',
    anomNetCash: 'Il Net Cash è negativo nel periodo: è uscito più denaro di quanto ne sia entrato.',
    sqWhy: 'Perché {name} è cambiato?',
    sqDriver: 'Cosa sta guidando {name} in questo momento?',
    sqBenchmark: 'Come si confronta con un benchmark sano?',
    summaryIntro: 'Ecco il quadro per {period}.',
    summaryHeadline: 'Il movimento più rilevante è {name}, {dir} del {pct}%.',
    moverLine: '{name} {dir} del {pct}%.',
    sqWhyMoved: 'Perché la metrica principale si è mossa?',
    sqTopBrand: 'Quale brand ha contribuito di più?',
    sqRisk: 'Ci sono rischi da tenere d’occhio nel periodo?',
    attrUpLead: 'L’aumento è trainato soprattutto da questi giocatori:',
    attrDownLead: 'Il calo è riconducibile soprattutto a questi giocatori:',
    attrConcentration: 'Insieme questi {n} giocatori pesano per circa il {pct}% della variazione.',
    attrBrandLed: '{brand} ha dato il contributo maggiore alla variazione.',
    sqTopPlayers: 'Mostra i giocatori che hanno contribuito di più',
  },
  es: {
    thisMetric: 'esta métrica',
    genericDef: '{name} es una métrica clave monitorizada en este panel.',
    trend: 'Está {dir} un {pct}% en {period}.',
    atValue: ' Ahora en {value}.',
    favourable: ' Es un movimiento favorable.',
    unfavourable: ' Es un movimiento desfavorable y conviene revisarlo.',
    valueNow: 'Actualmente marca {value}.',
    up: 'al alza', down: 'a la baja', flat: 'prácticamente estable', thePeriod: 'el periodo',
    volatile: '{name} ha sido volátil en la ventana reciente — valora la tendencia en todo el periodo, no en un solo día.',
    stable: '{name} se ha mantenido bastante estable día a día.',
    reQuestion: 'Sobre tu pregunta "{q}": esto es lo que muestran los datos actuales.',
    anomSwing: '{name} se movió un {pct}% en la dirección desfavorable — investiga la causa.',
    anomHold: 'El Hold % es {value}%, por debajo del ~30% de referencia: los pagos son altos frente a los depósitos.',
    anomBonus: 'El ratio Bono/GGR es {value}%, por encima del umbral de eficiencia del ~30% — las promociones pueden estar gastando de más.',
    anomNetCash: 'El Net Cash es negativo en el periodo: salió más dinero del que entró.',
    sqWhy: '¿Por qué cambió {name}?',
    sqDriver: '¿Qué está impulsando {name} ahora mismo?',
    sqBenchmark: '¿Cómo se compara con un benchmark saludable?',
    summaryIntro: 'Este es el panorama de {period}.',
    summaryHeadline: 'El mayor movimiento es {name}, {dir} un {pct}%.',
    moverLine: '{name} {dir} un {pct}%.',
    sqWhyMoved: '¿Por qué se movió la métrica principal?',
    sqTopBrand: '¿Qué marca contribuyó más?',
    sqRisk: '¿Algún riesgo que vigilar este periodo?',
    attrUpLead: 'El aumento se debe principalmente a estos jugadores:',
    attrDownLead: 'La caída se debe principalmente a estos jugadores:',
    attrConcentration: 'Juntos estos {n} jugadores explican ~{pct}% del cambio.',
    attrBrandLed: '{brand} aportó la mayor parte del cambio.',
    sqTopPlayers: 'Ver los jugadores que más contribuyeron',
  },
  pt: {
    thisMetric: 'esta métrica',
    genericDef: '{name} é uma métrica chave monitorada neste painel.',
    trend: 'Está {dir} {pct}% em {period}.',
    atValue: ' Agora em {value}.',
    favourable: ' É um movimento favorável.',
    unfavourable: ' É um movimento desfavorável e merece uma análise mais detida.',
    valueNow: 'Atualmente marca {value}.',
    up: 'em alta', down: 'em queda', flat: 'praticamente estável', thePeriod: 'o período',
    volatile: '{name} esteve volátil na janela recente — avalie a tendência no período inteiro, não num único dia.',
    stable: '{name} manteve-se bastante estável dia a dia.',
    reQuestion: 'Sobre a sua pergunta "{q}": isto é o que os dados atuais mostram.',
    anomSwing: '{name} moveu-se {pct}% na direção desfavorável — investigue a causa.',
    anomHold: 'O Hold % é {value}%, abaixo da referência de ~30%: os pagamentos estão altos frente aos depósitos.',
    anomBonus: 'O rácio Bónus/GGR é {value}%, acima do limiar de eficiência de ~30% — as promoções podem estar a gastar demais.',
    anomNetCash: 'O Net Cash está negativo no período: saiu mais dinheiro do que entrou.',
    sqWhy: 'Por que {name} mudou?',
    sqDriver: 'O que está a impulsionar {name} agora?',
    sqBenchmark: 'Como se compara com um benchmark saudável?',
    summaryIntro: 'Este é o panorama de {period}.',
    summaryHeadline: 'O maior movimento é {name}, {dir} {pct}%.',
    moverLine: '{name} {dir} {pct}%.',
    sqWhyMoved: 'Por que a métrica principal se moveu?',
    sqTopBrand: 'Qual marca contribuiu mais?',
    sqRisk: 'Algum risco a vigiar neste período?',
    attrUpLead: 'O aumento é impulsionado sobretudo por estes jogadores:',
    attrDownLead: 'A queda deve-se sobretudo a estes jogadores:',
    attrConcentration: 'Juntos estes {n} jogadores explicam ~{pct}% da variação.',
    attrBrandLed: '{brand} deu o maior contributo para a variação.',
    sqTopPlayers: 'Ver os jogadores que mais contribuíram',
  },
}

function tr(loc: Loc, key: string): string {
  return DP_PHRASES[loc][key] ?? DP_PHRASES.en[key] ?? key
}

// ---- attribution model -----------------------------------------------------
// Demo "who drove the change" analysis. For volume/revenue metrics we attribute
// the period-over-period delta to a deterministic set of top players (and the
// leading brand), so an answer like "turnover is up" is followed by *which
// players* caused it — the analytical depth a real assistant would give.

const DEMO_PLAYERS = [
  { id: '10482', name: 'vip_whale' },
  { id: '20917', name: 'highroller99' },
  { id: '33145', name: 'rio_gamer' },
  { id: '41882', name: 'lucky_seven' },
  { id: '55230', name: 'golden_ace' },
  { id: '61777', name: 'night_owl' },
  { id: '72604', name: 'slot_queen' },
  { id: '88391', name: 'bet_master' },
]

const DEMO_BRANDS = ['BetBoom', 'GalaxyBet', 'AurumPlay']

// Small stable string hash → [0,1) so player/brand selection is reproducible.
function hash01(key: string): number {
  let h = 2166136261
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return ((h >>> 0) % 100000) / 100000
}

// Metrics whose change can be attributed to individual players (money volume /
// revenue / cash flow). Ratios, percentages, RTP and margins are excluded —
// you cannot attribute a percentage to a player.
function isPlayerAttributable(metricKey?: string): boolean {
  if (!metricKey) return false
  if (/ratio|margin|rtp|pct|rate/.test(metricKey)) return false
  return /turnover|ggr|ngr|deposit|withdrawal|net_cash/.test(metricKey)
}

// Period-over-period delta in metric units (signed).
function metricDelta(s?: DPSnapshot): number | undefined {
  if (!s || typeof s.value !== 'number') return undefined
  if (typeof s.previous === 'number') return s.value - s.previous
  if (typeof s.change_pct === 'number' && s.change_pct !== -100) {
    const prev = s.value / (1 + s.change_pct / 100)
    return s.value - prev
  }
  return undefined
}

// Returns attribution insight lines (lead, per-player contributions,
// concentration, leading brand) or [] when not applicable.
function attributionInsights(loc: Loc, metricKey: string | undefined, s: DPSnapshot | undefined): string[] {
  if (!isPlayerAttributable(metricKey) || !s) return []
  const delta = metricDelta(s)
  if (delta === undefined || Math.abs(delta) < 1) return []

  const up = delta > 0
  const shares = [0.34, 0.25, 0.16] // top-3 explain ~75% of the move
  const start = Math.floor(hash01(metricKey || '') * DEMO_PLAYERS.length)
  const sign = up ? '+' : '-'

  const out: string[] = [tr(loc, up ? 'attrUpLead' : 'attrDownLead')]
  shares.forEach((share, i) => {
    const p = DEMO_PLAYERS[(start + i) % DEMO_PLAYERS.length]
    const amount = formatValue(Math.abs(delta) * share, s)
    out.push(`${p.name} (#${p.id}) · ${sign}${amount}`)
  })
  const pct = Math.round(shares.reduce((a, b) => a + b, 0) * 100)
  out.push(fmt(tr(loc, 'attrConcentration'), { n: shares.length, pct }))

  // Leading brand flourish for the core revenue/volume metrics.
  if (/turnover|ggr|ngr/.test(metricKey || '')) {
    const brand = DEMO_BRANDS[Math.floor(hash01('brand|' + metricKey) * DEMO_BRANDS.length)]
    out.push(fmt(tr(loc, 'attrBrandLed'), { brand }))
  }
  return out
}

function demoBrain(req: DPRequest): DPResponse {
  if (req.kind === 'summary') return demoSummary(req)
  return demoMetric(req)
}

function demoMetric(req: DPRequest): DPResponse {
  const loc = normLoc(req.locale)
  const def = glossaryFor(req.metric_id, req.metric_name)
  const name = req.metric_name || def?.name || tr(loc, 'thisMetric')
  const metricKey = def?.metric
  const pol = polarity(metricKey)
  const s = req.snapshot
  const parts: string[] = []
  const insights: string[] = []
  const anomalies: DPAnomaly[] = []
  const period = req.period || tr(loc, 'thePeriod')

  // Definition: English keeps the rich glossary description; other languages
  // use a clean localized sentence (the glossary itself is English-only).
  if (loc === 'en' && def) parts.push(`${name} is ${def.description.replace(/\.$/, '').toLowerCase()}.`)
  else parts.push(fmt(tr(loc, 'genericDef'), { name }))

  const pct = changePct(s)
  if (s && typeof pct === 'number') {
    const dir = pct > 0.5 ? tr(loc, 'up') : pct < -0.5 ? tr(loc, 'down') : tr(loc, 'flat')
    let sentence = fmt(tr(loc, 'trend'), { dir, pct: Math.abs(pct).toFixed(1), period })
    if (typeof s.value === 'number') sentence += fmt(tr(loc, 'atValue'), { value: formatValue(s.value, s) })
    if (pol !== undefined && Math.abs(pct) >= 1) {
      const good = pct > 0 === pol
      sentence += good ? tr(loc, 'favourable') : tr(loc, 'unfavourable')
    }
    parts.push(sentence)

    if (pol !== undefined) {
      const adverse = pct > 0 !== pol
      if (adverse && Math.abs(pct) >= 15) {
        anomalies.push({
          metric: name,
          severity: Math.abs(pct) >= 30 ? 'critical' : 'warning',
          message: fmt(tr(loc, 'anomSwing'), { name, pct: Math.abs(pct).toFixed(0) }),
        })
      }
    }
  } else if (s && typeof s.value === 'number') {
    parts.push(fmt(tr(loc, 'valueNow'), { value: formatValue(s.value, s) }))
  }

  if (s && typeof s.value === 'number' && metricKey) {
    if (metricKey.includes('hold') && s.value < 30) {
      anomalies.push({ metric: name, severity: 'warning', message: fmt(tr(loc, 'anomHold'), { value: s.value.toFixed(1) }) })
    }
    if (metricKey.includes('bonus_ggr') && s.value > 30) {
      anomalies.push({ metric: name, severity: 'warning', message: fmt(tr(loc, 'anomBonus'), { value: s.value.toFixed(1) }) })
    }
    if (metricKey.includes('net_cash') && s.value < 0) {
      anomalies.push({ metric: name, severity: 'critical', message: tr(loc, 'anomNetCash') })
    }
  }

  // Player-level attribution: who drove the change (turnover, GGR, deposits…).
  const attribution = attributionInsights(loc, metricKey, s ?? undefined)
  const attributable = attribution.length > 0
  if (attributable) insights.push(...attribution)

  if (s?.series && s.series.length >= 3) {
    const vals = s.series.map((p) => p.value)
    const min = Math.min(...vals)
    const max = Math.max(...vals)
    insights.push(fmt(tr(loc, min > 0 && max / min >= 1.5 ? 'volatile' : 'stable'), { name }))
  }

  if (req.question && req.question.trim()) {
    insights.unshift(fmt(tr(loc, 'reQuestion'), { q: req.question.trim() }))
  }

  const suggested = [
    fmt(tr(loc, 'sqWhy'), { name }),
    ...(attributable ? [tr(loc, 'sqTopPlayers')] : [fmt(tr(loc, 'sqDriver'), { name })]),
    tr(loc, 'sqBenchmark'),
  ]

  return {
    answer: parts.join(' '),
    insights,
    anomalies,
    suggested_questions: suggested,
    source: 'demo',
  }
}

function demoSummary(req: DPRequest): DPResponse {
  const loc = normLoc(req.locale)
  const period = req.period || tr(loc, 'thePeriod')
  const movers = (req.metrics || [])
    .map((m) => ({ name: m.metric_name || m.metric_id || tr(loc, 'thisMetric'), pct: changePct(m.snapshot), m }))
    .filter((x) => typeof x.pct === 'number') as { name: string; pct: number; m: DPNamedSnapshot }[]
  movers.sort((a, b) => Math.abs(b.pct) - Math.abs(a.pct) || a.name.localeCompare(b.name))

  const parts = [fmt(tr(loc, 'summaryIntro'), { period })]
  if (movers.length) {
    const top = movers[0]
    parts.push(fmt(tr(loc, 'summaryHeadline'), { name: top.name, dir: top.pct > 0 ? tr(loc, 'up') : tr(loc, 'down'), pct: Math.abs(top.pct).toFixed(1) }))
  }

  const insights = movers.slice(0, 4).map((m) =>
    fmt(tr(loc, 'moverLine'), { name: m.name, dir: m.pct > 0 ? tr(loc, 'up') : tr(loc, 'down'), pct: Math.abs(m.pct).toFixed(1) }),
  )

  // Attribute the single biggest mover to the players who drove it.
  if (movers.length) {
    const top = movers[0]
    const def = glossaryFor(top.m.metric_id, top.m.metric_name)
    insights.push(...attributionInsights(loc, def?.metric, top.m.snapshot))
  }

  const anomalies: DPAnomaly[] = []
  for (const m of req.metrics || []) {
    const r = demoMetric({ metric_id: m.metric_id, metric_name: m.metric_name, snapshot: m.snapshot, period, locale: req.locale })
    anomalies.push(...(r.anomalies || []))
  }

  return {
    answer: parts.join(' '),
    insights,
    anomalies,
    suggested_questions: [tr(loc, 'sqWhyMoved'), tr(loc, 'sqTopBrand'), tr(loc, 'sqRisk')],
    source: 'demo',
  }
}
